import React, { type FC, useRef, useState, type JSX } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { IconCircleInformation } from '@/app/common/icons/libraryIcons';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import { IconOrLegacyAsset } from '@/app/common/icons/IconOrLegacyAsset';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { parseAddLabel } from '../../../utils';
import { CheckboxToggleCount } from '../../getToggleField/Toggles';
import { DiscountToggleLabelContainer } from './styled';
import { IconWithTooltip, TooltipContent } from '../../../../../Tooltip/styled';
import { TooltipType } from '../../../../../Tooltip/TooltipType';

type DiscountToggleLabelProps = {
  label: string | JSX.Element;
  /** A named icon, or the basename of a multi-colour asset under `icons/`. */
  icon: StarfishIconRef | string;
  imageBasePath: string;
  count?: number;
  countOnTheLeft?: boolean;
  isDesktop?: boolean;
  tooltipText?: string;
  disabled?: boolean;
  isFirst?: boolean;
};

const DiscountToggleLabel: FC<DiscountToggleLabelProps> = ({
  label,
  icon,
  imageBasePath,
  count,
  countOnTheLeft,
  isDesktop,
  tooltipText,
  disabled,
  isFirst = false,
}): JSX.Element => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipSize = 209;
  const containerRef = useRef<HTMLDivElement>(null);
  const isTooltipTextLong = tooltipText && tooltipText.length > 30;
  const positionTooltipAtTheBottom = !!(isFirst && isTooltipTextLong);

  const handleShowTooltip = () => {
    const container = containerRef?.current?.parentNode?.parentNode?.parentNode?.parentNode
      ?.parentNode as HTMLDivElement;
    if (!container.classList.contains('overflow-visible')) {
      container.classList.add('overflow-visible');
    }
    setShowTooltip(true);
  };

  const handleHideTooltip = () => {
    const container = containerRef?.current?.parentNode?.parentNode?.parentNode?.parentNode
      ?.parentNode as HTMLDivElement;
    container.classList.remove('overflow-visible');
    setShowTooltip(false);
  };

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>) => {
    if (tooltipText) {
      e.preventDefault();
      e.stopPropagation();
      if (!isDesktop) {
        handleShowTooltip();
      }
    }
  };

  const handleOnMouseEnter = () => {
    if (isDesktop) {
      handleShowTooltip();
    }
  };

  const handleOnMouseLeave = () => {
    if (isDesktop) {
      handleHideTooltip();
    }
  };

  const tooltipRef = useOnclickOutside(() => {
    if (tooltipText && showTooltip) {
      handleHideTooltip();
    }
  });

  const tooltip = (
    <IconWithTooltip tooltipSize={tooltipSize} tooltipType={TooltipType.gray200} onClick={(e) => handleOnClick(e)}>
      <IconCircleInformation width={16} height={16} />
      {showTooltip && !disabled && (
        <TooltipContent
          ref={tooltipRef}
          tooltipSize={tooltipSize}
          className="tooltiptext"
          showOnBottom={positionTooltipAtTheBottom}
        >
          {tooltipText}
        </TooltipContent>
      )}
    </IconWithTooltip>
  );

  return (
    <DiscountToggleLabelContainer
      ref={containerRef}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
    >
      <IconOrLegacyAsset
        icon={typeof icon === 'string' ? `${imageBasePath}icons/${icon}.svg` : icon}
        size={20}
        alt="icon"
      />

      {tooltipText && typeof label === 'string' ? (
        <AddElementToLastWordInString text={label}>{tooltip}</AddElementToLastWordInString>
      ) : (
        <span>{label}</span>
      )}

      {count !== undefined ? (
        <CheckboxToggleCount countOnTheLeft={countOnTheLeft} className="checkbox-toggle-count">
          {parseAddLabel({ withBrackets: !!countOnTheLeft, count })}
        </CheckboxToggleCount>
      ) : null}
    </DiscountToggleLabelContainer>
  );
};

export default DiscountToggleLabel;
